import React, { useState } from 'react';
import { 
  CheckCircle2, 
  ShieldCheck, 
  Sparkles, 
  CreditCard, 
  Check, 
  ArrowRight, 
  HelpCircle,
  Lock,
  Users
} from 'lucide-react';
import { useStore } from '../store/useStore';
import { SubscriptionPlan } from '../types';

export const PricingPage: React.FC = () => {
  const { plans, currentPlanId, upgradePlan } = useStore();
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'annual'>('monthly');
  const [selectedPlanForCheckout, setSelectedPlanForCheckout] = useState<SubscriptionPlan | null>(null);
  const [cardNumber, setCardNumber] = useState('•••• •••• •••• 4242');
  const [cardExpiry, setCardExpiry] = useState('12/28');
  const [cardCvv, setCardCvv] = useState('883');
  const [isProcessing, setIsProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleCheckout = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPlanForCheckout) return;

    setIsProcessing(true);
    try {
      await upgradePlan(selectedPlanForCheckout.id, billingInterval);
      setSuccessMessage(`Successfully subscribed to ${selectedPlanForCheckout.name} (${billingInterval})!`);
      setTimeout(() => {
        setSelectedPlanForCheckout(null);
        setSuccessMessage(null);
      }, 1800);
    } catch (err) {
      console.error(err);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12">
      {/* Header */}
      <div className="text-center space-y-4 max-w-2xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-950 text-emerald-300 text-xs font-semibold border border-emerald-800">
          <ShieldCheck className="w-3.5 h-3.5" />
          <span>Australian Identity Protection Pricing</span>
        </div>
        <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight">
          Invest in Your Digital Peace of Mind
        </h1>
        <p className="text-slate-400 text-sm">
          Transparent pricing in Australian Dollars (AUD). No lock-in contracts. Cancel anytime with a single click.
        </p>

        {/* Monthly vs Annual Toggle */}
        <div className="inline-flex items-center gap-3 bg-slate-900 p-1.5 rounded-2xl border border-slate-800 shadow-lg text-xs font-semibold pt-1">
          <button
            type="button"
            onClick={() => setBillingInterval('monthly')}
            className={`px-4 py-2 rounded-xl transition-colors ${
              billingInterval === 'monthly' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'
            }`}
          >
            Monthly Billing
          </button>
          <button
            type="button"
            onClick={() => setBillingInterval('annual')}
            className={`px-4 py-2 rounded-xl transition-colors flex items-center gap-1.5 ${
              billingInterval === 'annual' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'
            }`}
          >
            <span>Annual (Save 18%)</span>
            <span className="px-1.5 py-0.5 rounded bg-emerald-400 text-slate-950 text-[10px] font-extrabold">2 MOS FREE</span>
          </button>
        </div>
      </div>

      {/* Pricing Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
        {plans.map((plan) => {
          const isCurrent = currentPlanId === plan.id;
          const price = billingInterval === 'annual' ? plan.annualPrice : plan.monthlyPrice;
          const displayPrice = plan.id === 'free' ? '$0' : `$${price.toFixed(2)}`;
          const periodText = plan.id === 'free' ? 'forever' : billingInterval === 'annual' ? '/ year (AUD)' : '/ month (AUD)';

          return (
            <div
              key={plan.id}
              className={`rounded-3xl p-8 flex flex-col justify-between shadow-2xl transition-all relative ${
                plan.isPopular
                  ? 'bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 border-2 border-emerald-500 ring-4 ring-emerald-500/20'
                  : 'bg-slate-900 border border-slate-800'
              }`}
            >
              {plan.isPopular && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-3.5 py-1 rounded-full bg-emerald-500 text-slate-950 text-xs font-extrabold uppercase tracking-wide shadow-md">
                  Most Popular For Australians
                </div>
              )}

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-white">{plan.name}</h3>
                  <p className="text-xs text-slate-400 mt-1 min-h-[32px]">{plan.tagline}</p>
                </div>

                <div className="flex items-baseline gap-1.5 border-b border-slate-800 pb-6">
                  <span className="text-4xl font-extrabold text-white tracking-tight">{displayPrice}</span>
                  <span className="text-xs text-slate-400 font-medium">{periodText}</span>
                </div>

                <div className="space-y-3 text-xs">
                  <span className="font-bold text-slate-300 uppercase tracking-wider text-[10px]">What's included:</span>
                  <ul className="space-y-2.5 text-slate-300">
                    {plan.features.map((feat, idx) => (
                      <li key={idx} className="flex items-start gap-2.5">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <span className="leading-snug">{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="pt-8">
                {isCurrent ? (
                  <div className="w-full py-3 text-center rounded-xl bg-slate-800 text-emerald-400 text-xs font-bold border border-slate-700">
                    ✓ Currently Active Plan
                  </div>
                ) : (
                  <button
                    onClick={() => setSelectedPlanForCheckout(plan)}
                    className={`w-full py-3 text-center rounded-xl font-bold text-xs shadow-lg transition-all flex items-center justify-center gap-2 ${
                      plan.isPopular
                        ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-emerald-950/40'
                        : 'bg-slate-800 hover:bg-slate-700 text-white'
                    }`}
                  >
                    <span>{plan.ctaText}</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Feature Comparison Matrix */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 shadow-2xl space-y-6">
        <h3 className="text-lg font-bold text-white">Full Feature Comparison Matrix</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-slate-800 text-slate-400">
                <th className="pb-3 font-semibold">Features & Capabilities</th>
                <th className="pb-3 font-semibold text-center">Free ($0)</th>
                <th className="pb-3 font-semibold text-center text-emerald-400">Personal ($9.95/mo)</th>
                <th className="pb-3 font-semibold text-center text-purple-400">Family ($19.95/mo)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800 text-slate-300">
              <tr>
                <td className="py-3">On-Demand Breach & Exposure Checker</td>
                <td className="py-3 text-center font-bold text-emerald-400">✓</td>
                <td className="py-3 text-center font-bold text-emerald-400">✓</td>
                <td className="py-3 text-center font-bold text-emerald-400">✓</td>
              </tr>
              <tr>
                <td className="py-3">AI Scam Message Analyzer (SMS / PayID / ATO)</td>
                <td className="py-3 text-center font-bold text-emerald-400">✓</td>
                <td className="py-3 text-center font-bold text-emerald-400">✓</td>
                <td className="py-3 text-center font-bold text-emerald-400">✓</td>
              </tr>
              <tr>
                <td className="py-3">Client-Side ID Watermark Generator</td>
                <td className="py-3 text-center font-bold text-emerald-400">✓</td>
                <td className="py-3 text-center font-bold text-emerald-400">✓</td>
                <td className="py-3 text-center font-bold text-emerald-400">✓</td>
              </tr>
              <tr>
                <td className="py-3">24/7 Automated Dark Web Surveillance</td>
                <td className="py-3 text-center text-slate-600">—</td>
                <td className="py-3 text-center font-bold text-emerald-400">✓ Continuous</td>
                <td className="py-3 text-center font-bold text-emerald-400">✓ Continuous</td>
              </tr>
              <tr>
                <td className="py-3">Australian Driver Licence & Passport Monitoring</td>
                <td className="py-3 text-center text-slate-600">—</td>
                <td className="py-3 text-center font-bold text-emerald-400">✓ DVS Monitored</td>
                <td className="py-3 text-center font-bold text-emerald-400">✓ DVS Monitored</td>
              </tr>
              <tr>
                <td className="py-3">8-Step Emergency Recovery Wizard</td>
                <td className="py-3 text-center text-slate-400">Basic Guide</td>
                <td className="py-3 text-center font-bold text-emerald-400">✓ Interactive Case</td>
                <td className="py-3 text-center font-bold text-emerald-400">✓ Interactive Case</td>
              </tr>
              <tr>
                <td className="py-3">Multi-Member Family Protection Shield</td>
                <td className="py-3 text-center text-slate-600">—</td>
                <td className="py-3 text-center text-slate-600">—</td>
                <td className="py-3 text-center font-bold text-purple-400">✓ Up to 5 Members</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Simulated Checkout Modal */}
      {selectedPlanForCheckout && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl space-y-6">
            <div className="flex items-center justify-between border-b border-slate-800 pb-4">
              <div>
                <h3 className="text-lg font-bold text-white">Upgrade to {selectedPlanForCheckout.name}</h3>
                <p className="text-xs text-slate-400">Billed {billingInterval} in AUD</p>
              </div>
              <button
                onClick={() => setSelectedPlanForCheckout(null)}
                className="text-slate-400 hover:text-white"
              >
                ✕
              </button>
            </div>

            {successMessage ? (
              <div className="p-6 rounded-2xl bg-emerald-950/80 border border-emerald-700 text-center space-y-3 animate-fade-in">
                <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
                <h4 className="text-base font-bold text-white">{successMessage}</h4>
                <p className="text-xs text-emerald-300">Your account benefits are now active.</p>
              </div>
            ) : (
              <form onSubmit={handleCheckout} className="space-y-4 text-xs">
                <div className="p-4 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-between">
                  <div>
                    <span className="font-bold text-white text-sm">{selectedPlanForCheckout.name} Plan</span>
                    <div className="text-slate-400 text-[11px]">Interval: {billingInterval}</div>
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-white text-base">
                      ${billingInterval === 'annual' ? selectedPlanForCheckout.annualPrice.toFixed(2) : selectedPlanForCheckout.monthlyPrice.toFixed(2)} AUD
                    </span>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block font-semibold text-slate-300 mb-1">Card Number:</label>
                    <div className="relative">
                      <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <input
                        type="text"
                        value={cardNumber}
                        onChange={(e) => setCardNumber(e.target.value)}
                        required
                        className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-slate-950 border border-slate-700 text-white font-mono text-xs focus:outline-none focus:border-emerald-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block font-semibold text-slate-300 mb-1">Expiry (MM/YY):</label>
                      <input
                        type="text"
                        value={cardExpiry}
                        onChange={(e) => setCardExpiry(e.target.value)}
                        required
                        className="w-full px-3 py-2.5 rounded-xl bg-slate-950 border border-slate-700 text-white font-mono text-xs focus:outline-none focus:border-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block font-semibold text-slate-300 mb-1">CVV Security Code:</label>
                      <input
                        type="password"
                        value={cardCvv}
                        onChange={(e) => setCardCvv(e.target.value)}
                        required
                        className="w-full px-3 py-2.5 rounded-xl bg-slate-950 border border-slate-700 text-white font-mono text-xs focus:outline-none focus:border-emerald-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-[11px] text-slate-400 pt-1">
                  <Lock className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                  <span>256-Bit SSL Encrypted Payment Simulation. No charges will be made.</span>
                </div>

                <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-800">
                  <button
                    type="button"
                    onClick={() => setSelectedPlanForCheckout(null)}
                    className="px-4 py-2 rounded-xl text-slate-300 hover:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isProcessing}
                    className="px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold transition-colors flex items-center gap-2"
                  >
                    {isProcessing ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Activating Plan...</span>
                      </>
                    ) : (
                      <>
                        <span>Activate Protection</span>
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
